import { Component, Input } from "@angular/core";

import { Product, ProductTemplateType } from "@hermes/api-model-product";
import { SpaceSeparatorPipe } from "@hermes/fragments/product-utils";

@Component({
    selector: "h-product-info-attributes",
    templateUrl: "./product-info-attributes.component.html",
    styleUrls: ["./product-info-attributes.component.scss"],
})
export class ProductInfoAttributesComponent {
    @Input()
    public borderBottom = false;

    @Input()
    public spacingBottom = true;

    @Input()
    public backgroundOpacity = false;

    @Input()
    public marginBottom = false;

    @Input()
    public borderTop = true;

    public madeInValue?: string;

    private inputProduct!: Product;

    constructor(private spaceSeparatorPipe: SpaceSeparatorPipe) {}

    public get finish(): string | undefined {
        return this.product.templateType === ProductTemplateType.Simple
            ? this.product?.simpleAttributes?.finish
            : this.product?.beltkitAttributes?.finishBuckle;
    }

    public get finishLabel(): string | undefined {
        return this.product[
            this.product.templateType === ProductTemplateType.Simple
                ? "simpleAttributes"
                : "beltkitAttributes"
        ]?.finishLabel;
    }

    public get product() {
        return this.inputProduct;
    }

    @Input()
    public set product(product: Product) {
        this.inputProduct = product;
        this.madeInValue = this.getMadeInValue(this.product);
    }

    /**
     * Retrieves the 'Made in' value for the given product.
     *
     * @param product
     * @returns The 'Made in' value as a string if available, otherwise undefined.
     */
    public getMadeInValue(product: Product): string | undefined {
        switch (product.templateType) {
            case ProductTemplateType.Simple: {
                return product.simpleAttributes?.madeIn;
            }
            case ProductTemplateType.Beltkit: {
                return this.getMergedMadeInValue(
                    product.beltkitAttributes?.madeIn,
                    product.beltkitAttributes?.objectTypeTitleBuckle,
                    product.beltkitAttributes?.madeInBuckle,
                    product.beltkitAttributes?.objectTypeTitleLeather,
                    product.beltkitAttributes?.madeInLeather,
                );
            }
            case ProductTemplateType.DoubleFragrance: {
                return this.getMergedMadeInValue(
                    product.doubleFragranceAttributes?.madeIn,
                    product.doubleFragranceAttributes?.objectTypeTitlePerfume,
                    product.doubleFragranceAttributes?.madeInPerfume,
                    product.doubleFragranceAttributes?.objectTypeTitleAccessory,
                    product.doubleFragranceAttributes?.madeInAccessory,
                );
            }

            case ProductTemplateType.AppleWatch: {
                const strapLabel = $localize`:@@hermes.product-page.color-watch-strap:Strap`;
                return product.appleWatchAttributes?.madeInStrap
                    ? this.spaceSeparatorPipe.transform(
                          strapLabel,
                          ":",
                          product.appleWatchAttributes.madeInStrap,
                      )
                    : undefined;
            }
            default: {
                return undefined;
            }
        }
    }

    public trackByAttributes(index: number): string {
        return index.toString();
    }

    private getMergedMadeInValue(
        madeIn: string | undefined,
        firstFragranceTitle: string | undefined,
        firstFragranceValue: string | undefined,
        secondFrangranceTitle: string | undefined,
        secondFrangranceValue: string | undefined,
    ): string | undefined {
        if (madeIn) {
            return madeIn;
        }

        const madeInParts: string[] = [];

        if (firstFragranceTitle && firstFragranceValue) {
            madeInParts.push(
                this.spaceSeparatorPipe.transform(
                    firstFragranceTitle,
                    ":",
                    firstFragranceValue,
                ),
            );
        }

        if (secondFrangranceTitle && secondFrangranceValue) {
            madeInParts.push(
                this.spaceSeparatorPipe.transform(
                    secondFrangranceTitle,
                    ":",
                    secondFrangranceValue,
                ),
            );
        }
        return madeInParts.length > 0 ? madeInParts.join("<br>") : undefined;
    }
}
